import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { ICtaForm } from '@campaign-form/campaign-form.interface';
import { CtaDetailsService } from '@campaign-form/form-fields/creators-and-ctas/cta-details.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { DeliverableUtilitiesService } from '@utilities/deliverable-utilities.service';
import { FormUtilitiesService } from '@utilities/form-utilities.service';
import { camelCase } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { ILastActiveElementDetails } from '../../../cta-details.interface';
import { IExecution } from '../../execution-step/execution-list/execution/execution.interface';
import { AudioTrackingComponent } from './audio-tracking/audio-tracking.component';
import { CaptionComponent } from './caption/caption.component';
import { ChatbotMultiComponent } from './chatbot-multi/chatbot-multi.component';
import { ChatbotComponent } from './chatbot/chatbot.component';
import { ContentComponent } from './content/content.component';
import { CustomComponent } from './custom/custom.component';
import { DeliverableConfigurationStore } from './deliverable-configuration.store';
import {
  EDeliverableCategory,
  EProcessingTime,
  IDeliverableConfiguration,
  IDeliverableConfigurationApi,
  IDeliverableItem,
  IDeliverableTypeConfig,
  IGroupedDeliverables,
  ITopLevelGroupedMenuDeliverableConfigs,
  TDeliverableForm,
} from './deliverable-list.interface';
import { DeliverableListService } from './deliverable-list.service';
import { DescriptionComponent } from './description/description.component';
import { GamePlayComponent } from './game-play/game-play.component';
import { HashtagComponent } from './hashtag/hashtag.component';
import { MentionComponent } from './mention/mention.component';
import { MessageComponent } from './message/message.component';
import { ProductPlacementComponent } from './product-placement/product-placement.component';
import { ShowcaseComponent } from './showcase/showcase.component';
import { TalkingPointAiComponent } from './talking-point-ai/talking-point-ai.component';
import { TalkingPointComponent } from './talking-point/talking-point.component';
import { TextTrackingComponent } from './text-tracking/text-tracking.component';
import { TitleComponent } from './title/title.component';
import { VisualComponent } from './visual/visual.component';

@Component({
  imports: [
    AudioTrackingComponent,
    CaptionComponent,
    ChatbotComponent,
    ChatbotMultiComponent,
    GamePlayComponent,
    HashtagComponent,
    MatButtonModule,
    MatMenuModule,
    MentionComponent,
    ProductPlacementComponent,
    ReactiveFormsModule,
    ShowcaseComponent,
    TalkingPointAiComponent,
    TalkingPointComponent,
    TextTrackingComponent,
    TitleComponent,
    TranslateModule,
    VisualComponent,
    DescriptionComponent,
    MessageComponent,
    MatTooltipModule,
    ContentComponent,
    IconComponent,
    CustomComponent,
  ],
  providers: [DeliverableConfigurationStore],
  selector: 'app-deliverable-list',
  templateUrl: './deliverable-list.component.html',
})
export class DeliverableListComponent {
  public readonly formArray = input.required<FormArray<FormGroup<TDeliverableForm>>>();

  public readonly deliverableConfigurations = input<IDeliverableConfigurationApi[] | null>(null);
  public readonly platformFeature = input<EPlatformFeature>();
  public readonly executionValues = input<IExecution[]>([]);
  public readonly creatorId = input<string | TNil>(null);
  public readonly isCustomMode = input<boolean>(false);
  public readonly creators = input<ICreator[]>([]);
  public readonly executionId = input<string | TNil>();
  public readonly isGlobalTracking = input<boolean>(false);
  public readonly isAllExpanded = input<boolean>(false);
  public readonly isViewMode = input<boolean>(false);
  public readonly showStatusBadge = input<boolean>(true);
  public readonly ctaForm = input<FormGroup<ICtaForm> | TNil>();
  public readonly isAnalyticsPlannedMode = input<boolean>(false);
  public readonly creatorBriefingMode = input<boolean>(false);

  // Wave 10 follow-up - ctaForm is already threaded down here for the "no name" button-disable
  // check above, so its own id doubles as ctaTemplateId for deliverable events without needing a
  // separate input from deliverable-step.
  public readonly ctaTemplateId = computed(() => this.ctaForm()?.get('id')?.value ?? undefined);

  public readonly isParentDeleteDisabled = signal<boolean>(true);
  public readonly lastActiveElementDetails = signal<Map<string, ILastActiveElementDetails>>(new Map());

  public readonly EDeliverableType = EDeliverableType;

  public readonly groupedDeliverables = computed<IGroupedDeliverables[]>(() => {
    this.formArrayUpdateTrigger();

    const grouped = this.formArray().controls.reduce((acc, control, index) => {
      const item = { control, index };

      if (!this.getExclusiveToCondition(item)) {
        return acc;
      }

      const type = control.get('type')!.value as EDeliverableType;

      if (!acc.has(type)) {
        acc.set(type, []);
      }

      acc.get(type)!.push(item);

      return acc;
    }, new Map<EDeliverableType, IDeliverableItem[]>());

    return Array.from(grouped.entries())
      .filter(([type, deliverables]) => {
        if (this.isAnalyticsPlannedMode() && this.deliverableUtilitiesService.isTrackingDeliverable(type)) {
          return false;
        }

        return deliverables.length > 0 && !!this.deliverableComponentMap[type];
      })
      .map(([type, deliverables]) => ({
        config: this.deliverableComponentMap[type],
        deliverables,
        type,
      }))
      .sort((a, b) =>
        (this.translate.instant(`shared.deliverableTypes.${a.type}`) as string).localeCompare(
          this.translate.instant(`shared.deliverableTypes.${b.type}`),
        ),
      );
  });

  public readonly displayDeliverableConfigs = computed(() =>
    this.deliverableConfigurations()
      ?.reduce((acc: IDeliverableConfiguration[], config) => {
        const originalTypeKey = camelCase(config.deliverable);
        const type = (this.deliverableConfigurationStore.keyMap[originalTypeKey] ||
          originalTypeKey) as EDeliverableType;
        const processingTimeKey = camelCase(config.processingTime);
        const platformFeatureType = camelCase(config.platformFeature) as EPlatformFeature;

        if (this.platformFeature() && this.platformFeature() !== platformFeatureType) {
          return acc;
        }

        acc.push({
          ...config,
          category: config.category ?? EDeliverableCategory.meta,
          isCheckedManually: config.isCheckedManually ?? false,
          originalTypeKey,
          platformFeatureType,
          processingTimeKey,
          type,
        });

        return acc;
      }, [])
      .sort((a, b) =>
        (this.translate.instant(`shared.deliverableTypes.${a.type}`) as string).localeCompare(
          this.translate.instant(`shared.deliverableTypes.${b.type}`),
        ),
      ),
  );

  public readonly allDeliverableConfigs = computed<IDeliverableConfiguration[]>(() => [
    ...(this.displayDeliverableConfigs() ?? []),
    ...this.globalDeliverableConfigs(),
  ]);
  public readonly groupedMenuDeliverableConfigs = computed<ITopLevelGroupedMenuDeliverableConfigs[]>(() => {
    const topLevelMap = new Map<boolean, Map<EDeliverableCategory, IDeliverableConfiguration[]>>();

    for (const config of this.allDeliverableConfigs()) {
      if (!topLevelMap.has(config.isCheckedManually)) {
        topLevelMap.set(config.isCheckedManually, new Map());
      }

      const categoryMap = topLevelMap.get(config.isCheckedManually)!;
      const category: EDeliverableCategory = config.category || EDeliverableCategory.meta;

      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }

      categoryMap.get(category)!.push(config);
    }

    return [false, true]
      .filter((key) => topLevelMap.has(key))
      .map((isCheckedManually) => ({
        categoryGroups: Array.from(topLevelMap.get(isCheckedManually)!.entries()).map(([category, configs]) => ({
          category,
          configs,
        })),
        isCheckedManually,
      }));
  });

  private static readonly HIGHLIGHT_NAMESPACE = 'deliverable';

  private readonly deliverableListService = inject(DeliverableListService);
  private readonly deliverableConfigurationStore = inject(DeliverableConfigurationStore);
  private readonly deliverableUtilitiesService = inject(DeliverableUtilitiesService);
  private readonly translate = inject(TranslateService);
  private readonly eventTrackingService = inject(EventTrackingService);

  private readonly globalDeliverableConfigs = computed<IDeliverableConfiguration[]>(() =>
    this.isGlobalTracking()
      ? [
          {
            active: true,
            category: EDeliverableCategory.meta,
            deliverable: EDeliverableType.audioTracking,
            id: uuidv4(),
            isCheckedManually: false,
            originalTypeKey: EDeliverableType.audioTracking,
            platformFeature: this.platformFeature()!,
            platformFeatureType: this.platformFeature()!,
            processingTime: EProcessingTime.nearTime,
            processingTimeKey: camelCase(EProcessingTime.nearTime),
            type: EDeliverableType.audioTracking,
          },
          {
            active: true,
            category: EDeliverableCategory.meta,
            deliverable: EDeliverableType.textTracking,
            id: uuidv4(),
            isCheckedManually: false,
            originalTypeKey: EDeliverableType.textTracking,
            platformFeature: this.platformFeature()!,
            platformFeatureType: this.platformFeature()!,
            processingTime: EProcessingTime.nearTime,
            processingTimeKey: camelCase(EProcessingTime.nearTime),
            type: EDeliverableType.textTracking,
          },
        ]
      : [],
  );

  private readonly formArrayUpdateTrigger = signal<number>(0);

  private readonly deliverableComponentMap: Record<EDeliverableType, IDeliverableTypeConfig> = {
    [EDeliverableType.audioTracking]: { component: AudioTrackingComponent, hasLabel: true },
    [EDeliverableType.caption]: { component: CaptionComponent, hasLabel: true },
    [EDeliverableType.chatBot]: { component: ChatbotComponent, hasLabel: false },
    [EDeliverableType.chatbotMulti]: { component: ChatbotMultiComponent, hasLabel: false },
    [EDeliverableType.gamePlay]: { component: GamePlayComponent, hasLabel: false },
    [EDeliverableType.hashTag]: { component: HashtagComponent, hasLabel: true },
    [EDeliverableType.mention]: { component: MentionComponent, hasLabel: true },
    [EDeliverableType.productPlacement]: { component: ProductPlacementComponent, hasLabel: true },
    [EDeliverableType.showcase]: { component: ShowcaseComponent, hasLabel: false, isChildOptional: true },
    [EDeliverableType.talkingPoint]: { component: TalkingPointComponent, hasLabel: true },
    [EDeliverableType.talkingPointAi]: { component: TalkingPointAiComponent, hasLabel: false },
    [EDeliverableType.textTracking]: { component: TextTrackingComponent, hasLabel: true },
    [EDeliverableType.title]: { component: TitleComponent, hasLabel: true },
    [EDeliverableType.visual]: { component: VisualComponent, hasLabel: false },
    [EDeliverableType.message]: { component: MessageComponent, hasLabel: true },
    [EDeliverableType.description]: { component: DescriptionComponent, hasLabel: true },
    [EDeliverableType.content]: { component: ContentComponent, hasLabel: false, isChildOptional: true },
    [EDeliverableType.custom]: { component: CustomComponent, hasLabel: false, isChildOptional: true },
  };

  constructor() {
    effect(() => {
      this.checkParentDeleteDisabled();
      this.formArrayUpdateTrigger.update((val) => val + 1);
    });

    effect((onCleanup) => {
      const formArray = this.formArray();
      const subscription = formArray.valueChanges.subscribe(() => {
        this.formArrayUpdateTrigger.update((val) => val + 1);
        this.checkParentDeleteDisabled();
      });

      onCleanup(() => {
        subscription.unsubscribe();
      });
    });
  }

  public trackByDeliverable(_: number, item: IDeliverableItem): string {
    return item.control.get('id')?.value ?? '';
  }

  public trackByDeliverableGroup(_: number, group: IGroupedDeliverables): EDeliverableType {
    return group.type;
  }

  public trackByMenuTopLevelGroup(_: number, topLevelGroup: ITopLevelGroupedMenuDeliverableConfigs): string {
    return String(topLevelGroup.isCheckedManually);
  }

  public getCategoryLabel(category: string): string {
    return category
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  public isNewlyAddedDeliverable(id: string): boolean {
    return FormUtilitiesService.isNewlyHighlightedElement(id, DeliverableListComponent.HIGHLIGHT_NAMESPACE);
  }

  public addDeliverable(type: EDeliverableType): void {
    const deliverableForm = this.deliverableListService.createDeliverable(
      type,
      undefined,
      this.isCustomMode() ? this.creatorId()! : undefined,
    );

    if (deliverableForm) {
      this.formArray().push(deliverableForm);
      this.formArrayUpdateTrigger.update((val) => val + 1);
      this.isParentDeleteDisabled.set(this.formArray().controls.length < 2);
      this.lastActiveElementDetails.set(
        CtaDetailsService.getLastActiveElementCreatorNames(this.formArray(), this.isCustomMode(), this.creators()),
      );

      FormUtilitiesService.highlightNewElement(
        deliverableForm,
        DeliverableListComponent.HIGHLIGHT_NAMESPACE,
        undefined,
        500,
        true,
      );

      // Fires immediately with the client-side id (no real DB id yet) - see
      // event-tracking-implementation-map.md "Deliverable _saved event design" for how this is
      // reconciled with the real id at save time, separately from this event.
      // campaignCreationIsOverride (audit round 3) - every event from the flow-dialog directory
      // reports it; campaignCreationCurrentStep is omitted for keyword-tracking rows (they're not
      // a flow-dialog step).
      this.eventTrackingService.track(EEventType.campaignCreationDeliverableAdded, {
        campaignCreationCurrentStep: this.isGlobalTracking() ? undefined : 2,
        campaignCreationIsOverride: this.isCustomMode(),
        creatorId: this.isCustomMode() ? (this.creatorId() ?? undefined) : undefined,
        ctaTemplateId: this.ctaTemplateId(),
        deliverableId: deliverableForm.get('id')?.value ?? undefined,
        deliverableType: type,
      });
    }
  }

  public removeDeliverable(id: string): void {
    const index = this.formArray().controls.findIndex((control) => control.get('id')?.value === id);

    if (index !== -1) {
      const removedControl = this.formArray().at(index);
      const removedType = removedControl.get('type')?.value as EDeliverableType | undefined;

      this.formArray().removeAt(index);
      this.formArrayUpdateTrigger.update((val) => val + 1);
      this.checkParentDeleteDisabled();

      this.eventTrackingService.track(EEventType.campaignCreationDeliverableRemoved, {
        campaignCreationCurrentStep: this.isGlobalTracking() ? undefined : 2,
        campaignCreationIsOverride: this.isCustomMode(),
        creatorId: this.isCustomMode() ? (this.creatorId() ?? undefined) : undefined,
        ctaTemplateId: this.ctaTemplateId(),
        deliverableId: id,
        deliverableType: removedType,
      });
    }
  }

  public checkParentDeleteDisabled(): void {
    this.isParentDeleteDisabled.set(
      CtaDetailsService.isLastCustomCtaElement(this.formArray(), this.isCustomMode(), this.creatorId()!),
    );
    this.lastActiveElementDetails.set(
      CtaDetailsService.getLastActiveElementCreatorNames(this.formArray(), this.isCustomMode(), this.creators()),
    );
  }

  private getExclusiveToCondition(item: IDeliverableItem): boolean {
    return (
      (item.control.get('exclusiveTo')?.value === this.creatorId() && this.isCustomMode()) ||
      !item.control.get('exclusiveTo')?.value ||
      this.isViewMode()
    );
  }
}
